import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardBody,
  Button,
  Spinner,
  Input,
  Label,
  FormGroup,
  Alert,
} from 'reactstrap';
import { FirebaseContext } from '../../components/Firebase';

interface UploadedFileEntry {
  name: string;
  size: number;
  status: 'queued' | 'uploading' | 'uploaded' | 'error';
  progress: number;
  downloadUrl?: string;
  storageRef?: string;
  trackId?: string;
  error?: string;
}

interface BroadcasterInfo {
  broadcasterOrgId: string;
  broadcasterOrgName: string;
}

interface OrgOption {
  id: string;
  name: string;
}

const RapidUploadPage: React.FC = () => {
  const { uploadCode } = useParams<{ uploadCode: string }>();
  const firebase = useContext(FirebaseContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [broadcaster, setBroadcaster] = useState<BroadcasterInfo | null>(null);

  const [campaign, setCampaign] = useState('');
  const [agency, setAgency] = useState('');
  const [agencyOrgId, setAgencyOrgId] = useState<string | null>(null);
  const [agencyOptions, setAgencyOptions] = useState<OrgOption[]>([]);
  const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');

  const [files, setFiles] = useState<UploadedFileEntry[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rawFilesRef = useRef<File[]>([]);

  // Validate upload code on mount
  useEffect(() => {
    const validate = async () => {
      if (!firebase || !uploadCode) {
        setError('Invalid upload link');
        setLoading(false);
        return;
      }
      try {
        const result = await firebase.validateUploadCode(uploadCode);
        if (!result.valid) {
          setError(result.error || 'Invalid upload link');
        } else {
          setBroadcaster({
            broadcasterOrgId: result.broadcasterOrgId!,
            broadcasterOrgName: result.broadcasterOrgName!,
          });
        }
      } catch (err: any) {
        setError(err.message || 'Error validating upload link');
      }
      setLoading(false);
    };
    validate();
  }, [firebase, uploadCode]);

  // Load agency options from the global agency collection (for autocomplete)
  useEffect(() => {
    const loadAgencies = async () => {
      if (!firebase) return;
      try {
        const { collection, getDocs } = await import('firebase/firestore');
        const snap = await getDocs(collection(firebase.db, 'agency'));
        const opts: OrgOption[] = snap.docs.map((d: any) => ({
          id: d.data().linkedOrgId || d.id,
          name: d.data().name || '',
        })).filter((o: OrgOption) => o.name);
        setAgencyOptions(opts);
      } catch { /* optional — public may not have read access */ }
    };
    loadAgencies();
  }, [firebase]);

  const filteredAgencies = agencyOptions.filter(opt =>
    opt.name.toLowerCase().includes(agency.toLowerCase()) && opt.name.toLowerCase() !== agency.toLowerCase()
  ).slice(0, 5);

  const handleAgencyChange = (value: string) => {
    setAgency(value);
    setAgencyOrgId(null); // Clear linked org when typing freely
    setShowAgencyDropdown(true);
  };

  const handleAgencySelect = (opt: OrgOption) => {
    setAgency(opt.name);
    setAgencyOrgId(opt.id);
    setShowAgencyDropdown(false);
  };

  const addFiles = (selected: FileList | File[]) => {
    const accepted = Array.from(selected)
      .filter(f => f.type.startsWith('audio/') || /\.(mp3|wav|aiff|flac)$/i.test(f.name));
    const newFiles: UploadedFileEntry[] = accepted.map(f => ({
      name: f.name,
      size: f.size,
      status: 'queued' as const,
      progress: 0,
    }));
    setFiles(prev => [...prev, ...newFiles]);
    rawFilesRef.current = [...rawFilesRef.current, ...accepted];
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    rawFilesRef.current = rawFilesRef.current.filter((_, i) => i !== index);
  };

  const canSubmit =
    !submitting &&
    !submitted &&
    campaign.trim().length > 0 &&
    senderName.trim().length > 0 &&
    senderEmail.trim().length > 0 &&
    files.length > 0 &&
    files.every(f => f.status !== 'uploading');

  const handleSubmit = async () => {
    if (!firebase || !broadcaster || !uploadCode || !canSubmit) return;
    setSubmitting(true);

    const rawFiles: File[] = rawFilesRef.current;

    const QC_SERVICE_URL = process.env.REACT_APP_QC_SERVICE_URL || 'http://localhost:8080';
    const { getDownloadURL, ref: storageRef, uploadBytes } = await import('firebase/storage');
    const { doc, collection } = await import('firebase/firestore');

    try {
      // Process each uploaded file — treat each as its own track
      // Follows the same flow as MultiFileUploader: if WAV-only, convert to MP3 via QC service,
      // upload both versions with versioned filenames (_v1_qc1)
      // IMPORTANT: storage folder name MUST match the future audiometa doc ID so the existing
      // delete cleanup and orphan-finder logic work the same as for normal uploads.
      const fileMetas: any[] = [];
      for (let i = 0; i < rawFiles.length; i++) {
        const f = rawFiles[i];
        setFiles(prev => prev.map((entry, idx) => idx === i ? { ...entry, status: 'uploading' } : entry));

        // Pre-generate a Firestore doc ID for the broadcaster's future audiometa doc.
        // This doc ID is the source of truth for: storage path, audiometa doc, masterCatalogue, publicCatalogue.
        const trackId = doc(collection(firebase.db, `organizations/${broadcaster.broadcasterOrgId}/audiometa`)).id;
        const isWav = /\.wav$/i.test(f.name);
        const baseName = f.name.replace(/\.[^/.]+$/, '');
        const version = 1;
        const qcVer = 1;

        // Collect files to upload for this track — may include converted MP3 if source is WAV
        let filesToUpload: File[] = [f];
        if (isWav) {
          // Convert WAV to MP3 via QC service (same endpoint as MultiFileUploader)
          try {
            const formData = new FormData();
            formData.append('file', f, f.name);
            const convertResponse = await fetch(`${QC_SERVICE_URL}/convert-to-mp3`, {
              method: 'POST',
              body: formData,
            });
            if (!convertResponse.ok) {
              throw new Error(`MP3 conversion failed: ${convertResponse.status}`);
            }
            const mp3Blob = await convertResponse.blob();
            const mp3DisplayName = f.name.replace(/\.wav$/i, '.mp3');
            const mp3AsFile = new File([mp3Blob], mp3DisplayName, { type: 'audio/mpeg' });
            filesToUpload = [f, mp3AsFile];
          } catch (err) {
            console.warn('MP3 conversion failed for', f.name, err);
            // Continue with WAV only
          }
        }

        // Upload each file with versioned filename (matches MultiFileUploader convention)
        const mp3Files: any[] = [];
        const wavFiles: any[] = [];
        let defaultMp3Id: string | null = null;

        for (const file of filesToUpload) {
          const ext = file.name.split('.').pop()!.toLowerCase();
          const versionedName = `${baseName}_v${version}_qc${qcVer}.${ext}`;
          const displayName = file.name;
          const fullPath = `rapid-upload/${broadcaster.broadcasterOrgId}/${trackId}/${versionedName}`;

          const fileToUpload = new File([file], versionedName, { type: file.type });
          await uploadBytes(
            storageRef(firebase.storage, fullPath),
            fileToUpload,
            { contentType: file.type || (ext === 'mp3' ? 'audio/mpeg' : 'audio/wav') }
          );
          const downloadUrl = await getDownloadURL(storageRef(firebase.storage, fullPath));

          const entry = {
            downloadUrl,
            storageRef: versionedName,
            fileName: displayName,
            version,
            qcVersion: qcVer,
            // Byte size so org storage usage can be summed on the
            // dashboard without listing Storage. New uploads only —
            // legacy entries omit it and count as 0.
            size: fileToUpload.size,
          };
          if (ext === 'mp3') {
            mp3Files.push(entry);
            if (!defaultMp3Id) defaultMp3Id = versionedName;
          } else if (ext === 'wav') {
            wavFiles.push(entry);
          }
        }

        fileMetas.push({
          trackId,
          trackName: baseName,
          mp3Files,
          wavFiles,
          defaultMp3Id,
        });

        setFiles(prev => prev.map((entry, idx) => idx === i ? { ...entry, status: 'uploaded', progress: 100 } : entry));
      }

      // Submit rapid upload metadata
      const result = await firebase.submitRapidUpload(uploadCode, {
        campaign: campaign.trim(),
        agency: agency.trim(),
        agencyOrgId: agencyOrgId,
        senderName: senderName.trim() || undefined,
        senderEmail: senderEmail.trim() || undefined,
        files: fileMetas,
      });

      if (!result.success) {
        setError(result.error || 'Upload failed');
        setSubmitting(false);
        return;
      }

      setSubmitted(true);
    } catch (err: any) {
      console.error('Rapid upload error:', err);
      setError(err.message || 'Upload failed');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner style={{ color: 'var(--accent-1-color)' }} />
      </div>
    );
  }

  if (error && !broadcaster) {
    return (
      <div className="wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card className="no-bg-card" style={{ maxWidth: '500px' }}>
          <CardBody className="text-center">
            <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '3rem', color: 'var(--button-danger)', marginBottom: '1rem' }} />
            <h4 style={{ color: '#fff' }}>Unable to load upload link</h4>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>{error}</p>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="wrapper" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Card className="no-bg-card" style={{ maxWidth: '500px' }}>
          <CardBody className="text-center">
            <i className="tim-icons icon-check-2" style={{ fontSize: '3rem', color: 'var(--button-success)', marginBottom: '1rem' }} />
            <h4 style={{ color: '#fff' }}>Upload Successful</h4>
            <p style={{ color: 'rgba(255,255,255,0.6)' }}>
              Your {files.length} file{files.length !== 1 ? 's have' : ' has'} been sent to <strong>{broadcaster?.broadcasterOrgName}</strong>.
              They will review and accept the upload before adding to their library.
            </p>
            <Button
              className="btn-primary-custom mt-3"
              onClick={() => {
                setSubmitted(false);
                setCampaign('');
                setAgency('');
                setAgencyOrgId(null);
                setSenderName('');
                setSenderEmail('');
                setFiles([]);
                rawFilesRef.current = [];
              }}
            >
              <span style={{ position: 'relative', zIndex: 2 }}>Upload More</span>
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="wrapper pt-4 pb-4">
      <Container style={{ maxWidth: '700px' }}>
        <Card className="no-bg-card">
          <CardBody>
            <div style={{ marginBottom: '1.5rem' }}>
              <h3 style={{ color: '#fff', marginBottom: '0.25rem' }}>Rapid Upload</h3>
              <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 0 }}>
                Send audio spots to <strong>{broadcaster?.broadcasterOrgName}</strong>. They will review and accept the upload before adding to their library.
              </p>
            </div>

            {error && (
              <Alert color="danger" toggle={() => setError(null)}>
                {error}
              </Alert>
            )}

            <FormGroup>
              <Label for="campaign" style={{ color: 'rgba(255,255,255,0.8)' }}>Campaign *</Label>
              <Input
                id="campaign"
                type="text"
                value={campaign}
                onChange={(e) => setCampaign(e.target.value)}
                placeholder="e.g. Summer Sale 2026"
                disabled={submitting}
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
              />
            </FormGroup>

            <FormGroup style={{ position: 'relative' }}>
              <Label for="agency" style={{ color: 'rgba(255,255,255,0.8)' }}>Company (optional)</Label>
              <Input
                id="agency"
                type="text"
                value={agency}
                onChange={(e) => handleAgencyChange(e.target.value)}
                onFocus={() => setShowAgencyDropdown(true)}
                onBlur={() => setTimeout(() => setShowAgencyDropdown(false), 200)}
                placeholder="Start typing to match an existing company..."
                disabled={submitting}
                style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
              />
              {showAgencyDropdown && filteredAgencies.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                  backgroundColor: '#1e1e2f', border: '1px solid rgba(255,255,255,0.15)',
                  borderRadius: '4px', maxHeight: '180px', overflowY: 'auto', marginTop: '2px',
                }}>
                  {filteredAgencies.map(opt => (
                    <div
                      key={opt.id}
                      onClick={() => handleAgencySelect(opt)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', color: '#fff', fontSize: '0.9rem',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      {opt.name}
                    </div>
                  ))}
                </div>
              )}
              {agencyOrgId && (
                <small style={{ color: '#50c878' }}>✓ Linked to existing company org</small>
              )}
            </FormGroup>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <FormGroup>
                <Label for="senderName" style={{ color: 'rgba(255,255,255,0.8)' }}>Your name *</Label>
                <Input
                  id="senderName"
                  type="text"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  disabled={submitting}
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
                />
              </FormGroup>
              <FormGroup>
                <Label for="senderEmail" style={{ color: 'rgba(255,255,255,0.8)' }}>Your email *</Label>
                <Input
                  id="senderEmail"
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  disabled={submitting}
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.15)' }}
                />
              </FormGroup>
            </div>

            {/* Drag-and-drop zone */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                marginTop: '1rem',
                padding: '2rem',
                border: `2px dashed ${dragOver ? 'var(--accent-1-color)' : 'rgba(255,255,255,0.2)'}`,
                borderRadius: '8px',
                textAlign: 'center',
                cursor: submitting ? 'not-allowed' : 'pointer',
                backgroundColor: dragOver ? 'rgba(255, 82, 82, 0.05)' : 'rgba(255,255,255,0.02)',
                transition: 'all 0.2s',
              }}
            >
              <i className="tim-icons icon-cloud-upload-94" style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }} />
              <div style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '0.25rem' }}>
                Drag audio files here or click to browse
              </div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                MP3, WAV, AIFF, FLAC accepted
              </div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="audio/*,.mp3,.wav,.aiff,.flac"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {files.map((f, idx) => (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '4px',
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: '#fff', fontSize: '0.9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.name}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                        {(f.size / 1024 / 1024).toFixed(2)} MB · {f.status}
                      </div>
                    </div>
                    {!submitting && (
                      <button
                        onClick={() => removeFile(idx)}
                        style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '1.1rem' }}
                      >
                        &times;
                      </button>
                    )}
                    {f.status === 'uploading' && (
                      <Spinner size="sm" style={{ color: 'var(--accent-1-color)' }} />
                    )}
                    {f.status === 'uploaded' && (
                      <i className="tim-icons icon-check-2" style={{ color: '#50c878' }} />
                    )}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                className="btn-primary-custom"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                <span style={{ position: 'relative', zIndex: 2 }}>
                  {submitting ? <><Spinner size="sm" className="mr-1" /> Uploading...</> : `Submit ${files.length > 0 ? `(${files.length} file${files.length !== 1 ? 's' : ''})` : ''}`}
                </span>
              </Button>
            </div>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default RapidUploadPage;
